import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackContext } from '@sg/garnish';

import { getUrqlError } from '@order/utils';

import {
  useAddAddressMutation,
  useDeleteAddressMutation,
  useUpdateAddressMutation,
} from '../../GraphQL/Address.generated';
import { useAddressFormTelemetry } from '../useAddressFormTelemetry';

import type { AddressForm } from '../../address.config';

export const useAddressHandlers = (params: UseAddressHandlersParams) => {
  const { editingAddressId, onSuccess } = params;

  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { trackSave, trackDelete, trackFail } = useAddressFormTelemetry();

  // ─── Mutations ──────────────────────────────────────────────────

  const [addAddressResponse, addAddress] = useAddAddressMutation();
  const [updateAddressResponse, updateAddress] = useUpdateAddressMutation();
  const [deleteAddressResponse, deleteAddress] = useDeleteAddressMutation();

  // ─── Loading States ─────────────────────────────────────────────

  const isAddingAddress = addAddressResponse.fetching;
  const isUpdatingAddress = updateAddressResponse.fetching;
  const isDeletingAddress = deleteAddressResponse.fetching;

  // ─── Helpers ────────────────────────────────────────────────────

  const notifyError = useCallback(
    (errorMessage: string) => {
      addNoticeBanner({
        text: errorMessage,
        palette: 'caution',
      });
    },
    [addNoticeBanner],
  );

  // ─── Address Validation ─────────────────────────────────────────

  const validateAddress = useCallback(
    (form: AddressForm) => {
      if (!form?.zipCode) {
        const message = formatMessage(messages.zipCodeInvalid);

        trackFail(message);
        notifyError(message);

        return false;
      }

      if (form?.street?.includes('undefined')) {
        const message = formatMessage(messages.addressInvalid);

        trackFail(message);
        notifyError(message);

        return false;
      }

      return true;
    },
    [formatMessage, trackFail, notifyError],
  );

  // ─── Callbacks ──────────────────────────────────────────────────

  const handleOnSave = useCallback(
    (form: AddressForm) => {
      (async () => {
        const isValidAddress = validateAddress(form);
        if (!isValidAddress) return;

        const input = { ...form, addressType: undefined };
        const response = await addAddress({ input });
        const data = response?.data?.addAddressV2;

        if (data?.__typename !== 'AddAddressSuccess') {
          const userMessage = formatMessage(messages.saveFailure);
          const systemMessage = getUrqlError(response.error, response.data as never);

          trackFail(userMessage, systemMessage);
          notifyError(userMessage);

          return;
        }

        trackSave(form);
        onSuccess?.();
      })();
    },
    [validateAddress, addAddress, formatMessage, trackFail, trackSave, notifyError, onSuccess],
  );

  const handleOnEdit = useCallback(
    (form: AddressForm) => {
      (async () => {
        if (!editingAddressId) return;

        const isValidAddress = validateAddress(form);
        if (!isValidAddress) return;

        const input = { ...form, addressType: undefined, id: editingAddressId };
        const response = await updateAddress({ input });
        const data = response.data?.updateAddressV2;

        if (data?.__typename !== 'UpdateAddressSuccess') {
          const userMessage = formatMessage(messages.saveFailure);
          const systemMessage = getUrqlError(response.error, response.data as never);

          trackFail(userMessage, systemMessage);
          notifyError(userMessage);

          return;
        }

        trackSave(form);
        onSuccess?.();
      })();
    },
    [
      editingAddressId,
      validateAddress,
      updateAddress,
      formatMessage,
      trackFail,
      trackSave,
      notifyError,
      onSuccess,
    ],
  );

  const handleOnDelete = useCallback(() => {
    (async () => {
      if (!editingAddressId) return;

      const response = await deleteAddress({ id: editingAddressId });
      const data = response?.data?.deleteAddress;

      if (data?.__typename !== 'DeleteAddressSuccess') {
        const userMessage = formatMessage(messages.deleteFailure);
        const systemMessage = getUrqlError(response.error, response.data as never);

        trackFail(userMessage, systemMessage);
        notifyError(userMessage);

        return;
      }

      trackDelete();
      onSuccess?.();
    })();
  }, [
    editingAddressId,
    deleteAddress,
    formatMessage,
    trackFail,
    trackDelete,
    notifyError,
    onSuccess,
  ]);

  return {
    handleOnSave,
    handleOnEdit,
    handleOnDelete,
    isAddingAddress,
    isUpdatingAddress,
    isDeletingAddress,
  };
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  saveFailure: {
    defaultMessage: "Oops, we couldn't save your address, please try again.",
    description: 'Address Handlers | Save failure',
  },
  deleteFailure: {
    defaultMessage: "Oops, we couldn't remove your address, please try again.",
    description: 'Address Handlers | Delete failure',
  },
  addressInvalid: {
    defaultMessage: 'This address is not valid, please provide a street number.',
    description: 'Address Handlers | Invalid address',
  },
  zipCodeInvalid: {
    defaultMessage: 'This address is not valid, please provide a zip code.',
    description: 'Address Handlers | Invalid zip code',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type UseAddressHandlersParams = Readonly<{
  editingAddressId?: string;
  onSuccess?: () => void;
}>;
